import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

// FAD-07 Semanal - Resumo do Laboratorio (campos 45 a 65). SOMENTE LEITURA (Sessao 27, 21/09/2026).
// Os numeros vem do servidor (/api/fad07/dados -> computado), somados dos resultados de
// laboratorio lancados no FAD-01 (verso do boletim). Nada aqui e digitado nem salvo.
// Regras do Walber (18 e 21/09/2026):
//   - aegypti e albopictus em VERMELHO negrito (positividade); "Outros" em AZUL negrito;
//   - campos 63/64/65 sem repetir: 63 = so aegypti, 64 = so albopictus, 65 = as duas;
//   - ate 20 quarteiroes por campo, igual ao papel, sem aviso quando passar.

type Num = Record<string, number | undefined>;

interface LabComputado {
  lab_dep_aeg?: Num; lab_dep_alb?: Num;
  lab_imov_aeg?: Num; lab_imov_alb?: Num; lab_imov_outros?: Num;
  lab_ex_aeg?: Num; lab_ex_alb?: Num; lab_ex_outros?: Num;
  lab_qrt_aegypti?: string[]; lab_qrt_albopictus?: string[]; lab_qrt_intersecao?: string[];
  amostras_coletadas?: number;
}

interface Props { dados: unknown }

const VERMELHO = '#C62828';
const AZUL = '#1565C0';
const CINZA = '#9aa5b1';
const BORDA = '1px solid #cfd8e3';

const DEP: string[] = ['a1', 'a2', 'b', 'c', 'd1', 'd2', 'e', 'total'];
const IMOV: string[] = ['r', 'c', 'tb', 'pe', 'o', 'total'];
const EX: string[] = ['larvas', 'pupas', 'exuvias', 'adultos'];

const TITULO = {
  fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
  mt: 3, mb: 1, display: 'block',
} as const;
const CAB = { fontWeight: 700, border: BORDA, background: '#f3f6fa', p: 0.75, textAlign: 'center' } as const;
const ROT = { whiteSpace: 'nowrap', border: BORDA, pr: 2, fontWeight: 600 } as const;

const num = (o: Num | undefined, k: string): number => Number((o && o[k]) || 0);

const LEGENDA_VERMELHA: React.CSSProperties = { color: VERMELHO, fontWeight: 800 };
const LEGENDA_AZUL: React.CSSProperties = { color: AZUL, fontWeight: 800 };

function Celula({ v, cor }: { v: number; cor: string }) {
  return (
    <TableCell
      align="center"
      sx={{
        border: BORDA, p: 0.75, minWidth: 46, fontSize: 16,
        fontWeight: v > 0 ? 800 : 400,
        color: v > 0 ? cor : CINZA,
      }}
    >
      {v > 0 ? v : '–'}
    </TableCell>
  );
}

function Grade({ titulo, lista, cor }: { titulo: string; lista: string[] | undefined; cor: string }) {
  const itens = (lista || []).slice(0, 20);
  return (
    <>
      <Typography variant="caption" sx={TITULO}>{titulo}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
        {Array.from({ length: 20 }, (_, i) => (
          <Box
            key={i}
            sx={{
              border: BORDA, borderRadius: 1, minHeight: 38,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
              fontWeight: itens[i] ? 800 : 400,
              color: itens[i] ? cor : CINZA,
            }}
          >
            {itens[i] || '/'}
          </Box>
        ))}
      </Box>
    </>
  );
}

export default function FAD07Laboratorio({ dados }: Props) {
  const c = (dados as { computado?: LabComputado } | null | undefined)?.computado;

  if (!c) {
    return (
      <Typography variant="body2" sx={{ color: '#4a5d75' }}>
        Escolha a área, o ciclo e a semana para ver o resumo do laboratório.
      </Typography>
    );
  }

  const linhasDep: { rotulo: string; dados: Num | undefined }[] = [
    { rotulo: 'com Aedes aegypti', dados: c.lab_dep_aeg },
    { rotulo: 'com Aedes albopictus', dados: c.lab_dep_alb },
  ];
  const linhasImov: { rotulo: string; imov: Num | undefined; ex: Num | undefined; cor: string }[] = [
    { rotulo: 'com Aedes aegypti', imov: c.lab_imov_aeg, ex: c.lab_ex_aeg, cor: VERMELHO },
    { rotulo: 'com Aedes albopictus', imov: c.lab_imov_alb, ex: c.lab_ex_alb, cor: VERMELHO },
    { rotulo: 'Outros', imov: c.lab_imov_outros, ex: c.lab_ex_outros, cor: AZUL },
  ];

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#4a5d75', mb: 1 }}>
        Preenchido pelo sistema com os resultados lançados no FAD-01 (laboratório). Para corrigir, altere o boletim.
        {' '}Amostras coletadas na semana: <strong>{c.amostras_coletadas ?? 0}</strong>.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1, fontSize: 13 }}>
        <span style={LEGENDA_VERMELHA}>■ Vermelho: Ae. aegypti e Ae. albopictus (positividade)</span>
        <span style={LEGENDA_AZUL}>■ Azul: outros</span>
      </Box>

      <Typography variant="caption" sx={{ ...TITULO, mt: 1 }}>
        Nº depósitos com espécimes por tipo (45-52)
      </Typography>
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Table size="small" sx={{ minWidth: 700, borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={CAB} />
              {['A1', 'A2', 'B', 'C', 'D1', 'D2', 'E', 'Total'].map(h => (
                <TableCell key={h} sx={CAB}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {linhasDep.map(l => (
              <TableRow key={l.rotulo}>
                <TableCell sx={{ ...ROT, color: VERMELHO }}>{l.rotulo}</TableCell>
                {DEP.map(k => <Celula key={k} v={num(l.dados, k)} cor={VERMELHO} />)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Typography variant="caption" sx={TITULO}>
        Nº de imóveis com espécies, por tipo (53-58) + Nº de exemplares (59-62)
      </Typography>
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Table size="small" sx={{ minWidth: 780, borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={CAB} />
              {['Residência', 'Comércio', 'TB', 'PE', 'Outros', 'Total', 'Larvas', 'Pupas', 'Exúvia de pupas', 'Adultos'].map(h => (
                <TableCell key={h} sx={CAB}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {linhasImov.map(l => (
              <TableRow key={l.rotulo}>
                <TableCell sx={{ ...ROT, color: l.cor }}>{l.rotulo}</TableCell>
                {IMOV.map(k => <Celula key={`i${k}`} v={num(l.imov, k)} cor={l.cor} />)}
                {EX.map(k => <Celula key={`e${k}`} v={num(l.ex, k)} cor={l.cor} />)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Grade titulo="Nº e seq. dos quarteirões com Aedes aegypti (63)" lista={c.lab_qrt_aegypti} cor={VERMELHO} />
      <Grade titulo="Nº e seq. dos quarteirões com Aedes albopictus (64)" lista={c.lab_qrt_albopictus} cor={VERMELHO} />
      <Grade titulo="Nº e seq. dos quarteirões com Aedes aegypti e Aedes albopictus (65)" lista={c.lab_qrt_intersecao} cor={VERMELHO} />
    </Box>
  );
}
